import React, { type ReactNode, useState } from 'react';

import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Toggle from '@amzn/meridian/toggle';
import _sortBy from 'lodash/sortBy';
import _toLower from 'lodash/toLower';
import _upperFirst from 'lodash/upperFirst';
import ConfirmModal from 'src/components/shared/confirmModal/confirmModal';
import Grid from 'src/components/shared/grid/grid';
import { TOAST_TIMEOUT } from 'src/config/Toast';
import useOnDone from 'src/hooks/useOnDone';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { createToast } from 'src/store/toastsSlice';
import { updateFeatureFlag } from 'src/store/userSlice';
import { getObjectEntries } from 'src/utils/helpers';

// **** Constants **** //

const MODAL_DESCRIPTIONS = {
  Update(isEnabled: boolean, featureDisplayName: string): ReactNode {
    return (
      <>
        Are you sure you want to {isEnabled ? 'enable' : 'disable'} the <b>{featureDisplayName}</b> feature?
      </>
    );
  },
} as const;

// **** Types **** //

interface ModalState {
  isOpen: boolean;
  description: string | ReactNode;
  featureName: string;
  isEnabled?: boolean;
}

// **** Components **** //

/**
 * Default Comonent
 */
const FeatureFlags = () => {
  const dispatch = useAppDispatch();
  const { isLoadingUpdateFeatureFlag, updateFeatureFlagErrorMessage, featureFlags, isFeatureFlagsLoaded } =
    useAppSelector((state) => state.user);
  const [disableToggles, setDisableToggles] = useState(false);
  const [modalConfirmationState, setModalConfirmationState] = useState<ModalState>({
    isOpen: false,
    description: '',
    featureName: '',
  });

  // Derive a readable name from the flag key, eg. TASK_GROUPING -> "Task Grouping". Splitting on
  // the API's underscore keeps digits attached to their word, eg. "T2 Plus Experience"
  const getFeatureDisplayName = (featureName: string) => _toLower(featureName).split('_').map(_upperFirst).join(' ');

  // Sorted alphabetically, widened because state also holds flags the frontend does not type
  const featureFlagRows = _sortBy(
    getObjectEntries(featureFlags as Record<string, boolean>).map(([key, isEnabled]) => ({
      key,
      isEnabled,
      name: getFeatureDisplayName(key),
    })),
    'name',
  );

  // Confirm Update
  const handleToggleClick = (featureDisplayName: string, featureName: string, isEnabled: boolean) =>
    setModalConfirmationState({
      isOpen: true,
      description: MODAL_DESCRIPTIONS.Update(isEnabled, featureDisplayName),
      featureName,
      isEnabled,
    });

  // Close "Confirmation Modal"
  const closeModal = () =>
    setModalConfirmationState({
      isOpen: false,
      description: '',
      featureName: '',
    });

  // When "Confirm" is clicked inside the confirmation modal
  const onConfirmClick = () => {
    const { featureName, isEnabled } = modalConfirmationState;
    setDisableToggles(true);
    const apiFeatureName = _toLower(featureName);
    dispatch(updateFeatureFlag({ featureName: apiFeatureName, isEnabled: !!isEnabled }));
  };

  // When finished "Updating" a feature flag
  useOnDone(() => {
    if (!!updateFeatureFlagErrorMessage) {
      createToast({
        type: 'error',
        message: updateFeatureFlagErrorMessage,
        timeout: TOAST_TIMEOUT,
      });
    } else {
      setModalConfirmationState({
        isOpen: false,
        description: '',
        featureName: '',
      });
    }
    setDisableToggles(false);
  }, isLoadingUpdateFeatureFlag);

  return (
    <Column spacingInset="400" spacing="400">
      {/* Header */}
      <Row>
        <Heading level={3}>Feature Flags</Heading>
      </Row>

      {/* Wait for the API so the list is not built from default values */}
      {!isFeatureFlagsLoaded && (
        <Row alignmentHorizontal="center">
          <Loader />
        </Row>
      )}

      {/* Feature Flag List, filled left to right so the alphabetical order still reads normally */}
      {isFeatureFlagsLoaded && (
        <Grid columns={3}>
          {featureFlagRows.map(({ key, name, isEnabled }) => (
            <Row key={key} alignmentHorizontal="justify" alignmentVertical="center" type="fill" spacingInset="400">
              <Text>{name}</Text>
              <Toggle
                checked={isEnabled}
                onChange={() => handleToggleClick(name, key, !isEnabled)}
                disabled={disableToggles}
              />
            </Row>
          ))}
        </Grid>
      )}

      {/* Modals */}
      <ConfirmModal
        modalDescription={modalConfirmationState.description}
        open={modalConfirmationState.isOpen}
        setOpen={closeModal}
        onConfirm={onConfirmClick}
        showCancelButton={true}
        showSpinner={isLoadingUpdateFeatureFlag}
        disableAutoClose={true}
      />
    </Column>
  );
};

// **** Export **** //

export default FeatureFlags;
